import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { useWindowStore } from '../../stores';
import './DetailTable.scss';

const DetailTable = ({
  id,
  listData,
  tableHeadData,
  tableRowData,
  tableDescription,
  tableNote,
  className,
}) => {
  if (
    (listData?.length === 0)
    && (!tableDescription || tableDescription === '')
    && (tableRowData?.length === 0)
    && (tableHeadData?.length === 0)
  ) {
    return null;
  }
  const isMobile = useWindowStore((state) => state.isMobile);

  return (
    <div
      id={id || ''}
      className={classNames('detailSection detailTable', {
        [className]: className,
        'detailTable--list': listData?.length > 0 && (tableRowData?.length === 0 && tableHeadData?.length === 0),
      })}
    >
      { listData?.length > 0 && (
        <div
          className={classNames('detailTable__list content-fadeIn', {
            'detailTable__list--oneItem': listData?.length === 1,
          })}
        >
          { listData.map((stat, index) => {
            // Create a unique key - use stat.key if available (for skeletons), otherwise use label/data
            const identifier = stat.key || stat.label || stat.data || 'item';
            const key = `${identifier}-${index}`;
            return (
              <div
                className="detailTable__item detailTable__item--list"
                key={key}
              >
                <div className="detailTable__itemWrapper">

                  {stat.label && (
                  <div className="detailTable__itemLabel detailTable__itemLabel--list">
                    { stat.label }
                    :
                  </div>
                  )}

                  {stat.data && (
                  <div className="detailTable__itemData" dangerouslySetInnerHTML={{ __html: stat.data }} />
                  )}

                  { stat.note && (
                  <div className="detailTable__itemNote">
                    { stat.note }
                  </div>
                  )}

                  { stat.component && (
                  <div className="detailTable__itemComponent">
                    { stat.component }
                  </div>
                  )}

                </div>
              </div>
            );
          })}
        </div>
      )}

      { (tableHeadData?.length > 0 || tableRowData?.length > 0) && (
        <div className="detailTable__scroll">
          <table className="detailTable__table content-fadeIn">

            { // This is the same data as the mobile itemLabels.
              !isMobile && tableHeadData?.length > 0 && (
              <thead>
                <tr>
                  { tableHeadData?.map((label, index) => (label !== '' ? (
                    <th key={`header-${label}-${index}`} className="detailTable__item detailTable__item--header">
                      { label }
                    </th>
                  ) : (<th key={`empty-${index}`} />)))}
                </tr>
              </thead>
              )
            }

            <tbody>
              { tableRowData?.map((row, rowIndex) => (
                <tr
                  key={row.key || row.label || `row-${rowIndex}`}
                  className={classNames({ [row.className]: row.className })}
                  onClick={row.onClick}
                  data-testid={row.testId}
                >

                  { row?.label && (
                    <td className="detailTable__item detailTable__item--header detailTable__item--left">
                      { row.label }
                    </td>
                  )}

                  { row?.cells?.map((cell, cellIndex) => (
                    <td
                      key={cell.key || cell.mobileLabel || `cell-${rowIndex}-${cellIndex}`}
                      className={classNames('detailTable__item detailTable__item--cell', {
                        [cell.className]: cell.className,
                      })}
                    >

                      { isMobile && cell.mobileLabel && (
                        <div className="detailTable__itemLabel detailTable__itemLabel--subheader">
                          {cell.mobileLabel}
                        </div>
                      )}

                      { cell.data && (
                        <div className="detailTable__itemWrapper">
                          {cell.data}
                        </div>
                      )}

                      { cell.note && (
                        <div className="detailTable__itemNote">
                          { cell.note }
                        </div>
                      )}

                    </td>
                  ))}

                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      { tableNote && (
        <p className="detailTable__note content-fadeIn">
          { tableNote }
        </p>
      )}

      { tableDescription && (
        <p className="detailTable__description content-fadeIn">
          { tableDescription }
        </p>
      )}

    </div>
  );
};

DetailTable.propTypes = {
  id: PropTypes.string,
  listData: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      data: PropTypes.string,
      note: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
      component: PropTypes.element,
    }),
  ),
  tableHeadData: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  ),
  tableRowData: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      className: PropTypes.string,
      onClick: PropTypes.func,
      testId: PropTypes.string,
      cells: PropTypes.arrayOf(
        PropTypes.shape({
          mobileLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
          data: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
          note: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
          className: PropTypes.string,
        }),
      ),
    }),
  ),
  tableDescription: PropTypes.string,
  tableNote: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  className: PropTypes.string,
};

DetailTable.defaultProps = {
  id: null,
  listData: null,
  tableHeadData: null,
  tableRowData: null,
  tableDescription: null,
  tableNote: null,
  className: null,
};

export default DetailTable;
